import { Table, ActionIcon, Group, Text, Badge, Skeleton, Stack, Menu } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconDotsVertical } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import type { MouseEvent } from "react";
import type { EmailAccount } from "../../schema/email-account.types";
import { EmailAccountStatusBadge } from "../email-account-status-badge";
import { ProviderIcon } from "../provider-icon";

/**
 * Email Accounts Table Component
 *
 * Displays a table of email accounts with actions for managing them.
 * Includes columns for email, provider, status, default flag, and action buttons.
 */

/**
 * Props for EmailAccountsTable
 */
interface EmailAccountsTableProperties {
  /** Array of email accounts to display */
  accounts: EmailAccount[];
  /** Loading state */
  loading?: boolean;
  /** Callback when reconnect is clicked */
  onReconnect?: (account: EmailAccount) => void;
  /** Callback when delete is clicked */
  onDelete?: (account: EmailAccount) => void;
  /** Callback when set as default is clicked */
  onSetDefault?: (account: EmailAccount) => void;
}

/* eslint-disable no-magic-numbers */
/**
 * Loading skeleton for table
 */
function TableSkeleton() {
  return (
    <Table.Tbody>
      {[1, 2, 3].map((index) => (
        <Table.Tr key={index}>
          <Table.Td>
            <Skeleton height={20} width="80%" />
          </Table.Td>
          <Table.Td>
            <Skeleton height={20} width={60} />
          </Table.Td>
          <Table.Td>
            <Skeleton height={20} width={80} />
          </Table.Td>
          <Table.Td>
            <Skeleton height={20} width={40} />
          </Table.Td>
          <Table.Td>
            <Skeleton height={20} width={100} />
          </Table.Td>
        </Table.Tr>
      ))}
    </Table.Tbody>
  );
}

/**
 * Empty state for table
 */
function EmptyState() {
  return (
    <Table.Tbody>
      <Table.Tr>
        <Table.Td colSpan={5}>
          <Stack align="center" gap="md" py="xl">
            <Text c="dimmed" size="sm">
              Nenhuma conta de email configurada
            </Text>
            <Text c="dimmed" size="xs">
              Clique em *Nova Conta* para adicionar sua primeira conta
            </Text>
          </Stack>
        </Table.Td>
      </Table.Tr>
    </Table.Tbody>
  );
}

/**
 * EmailAccountsTable Component
 * @param root0
 * @param root0.accounts
 * @param root0.loading
 * @param root0.onReconnect
 * @param root0.onDelete
 * @param root0.onSetDefault
 * @example
 * <EmailAccountsTable
 *   accounts={accounts}
 *   loading={loading}
 *   onReconnect={(account) => handleReconnect(account.id)}
 *   onDelete={(account) => handleDelete(account.id)}
 *   onSetDefault={(account) => handleSetDefault(account.id)}
 * />
 */
export function EmailAccountsTable({
  accounts,
  loading = false,
  onReconnect,
  onDelete,
  onSetDefault
}: EmailAccountsTableProperties) {
  const accountsById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);

  /**
   * Handle delete with confirmation modal
   * @param account
   */
  const handleDelete = useCallback(
    (account: EmailAccount) => {
      modals.openConfirmModal({
        title: "Remover conta de email",
        children: (
          <Text size="sm">
            Tem certeza que deseja remover a conta <strong>{account.email}</strong>?
            <br />
            Esta ação não pode ser desfeita.
          </Text>
        ),
        labels: { confirm: "Remover", cancel: "Cancelar" },
        confirmProps: { color: "red" },
        onConfirm: () => onDelete?.(account)
      });
    },
    [onDelete]
  );

  /**
   * Handle set as default with confirmation if changing default
   * @param account
   */
  const handleSetDefault = useCallback(
    (account: EmailAccount) => {
      if (account.is_default) {
        return; // Already default
      }

      modals.openConfirmModal({
        title: "Definir como conta padrão",
        children: (
          <Text size="sm">
            Deseja definir <strong>{account.email}</strong> como a conta padrão para envio de emails?
          </Text>
        ),
        labels: { confirm: "Confirmar", cancel: "Cancelar" },
        confirmProps: { color: "primary" },
        onConfirm: () => onSetDefault?.(account)
      });
    },
    [onSetDefault]
  );

  /**
   * Handle menu actions with account lookup
   * @param event
   */
  const handleMenuAction = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const { accountId, action } = event.currentTarget.dataset;
      if (!accountId || !action) {
        return;
      }

      const account = accountsById.get(accountId);
      if (!account) {
        return;
      }

      if (action === "reconnect") {
        onReconnect?.(account);
        return;
      }

      if (action === "default") {
        handleSetDefault(account);
        return;
      }

      if (action === "delete") {
        handleDelete(account);
      }
    },
    [accountsById, handleDelete, handleSetDefault, onReconnect]
  );

  /**
   * Render action buttons based on account status
   * @param account
   */
  const renderActions = (account: EmailAccount) => {
    const canReconnect = account.status === "pending" || account.status === "expired" || account.status === "error";
    const isPending = account.status === "pending";

    // Button label and color based on status
    const reconnectLabel = isPending ? "Autorizar" : "Reconectar";
    const defaultLabel = account.is_default ? "Conta padrão" : "Definir como padrão";
    const canSetDefault = !account.is_default && account.status === "connected";
    const accountId = account.id;

    return (
      <Group gap="xs" justify="flex-start" wrap="nowrap">
        <Menu shadow="md" width={200} position="left" withArrow arrowPosition="center">
          <Menu.Target>
            <ActionIcon variant="subtle" aria-label="Ações" color="gray">
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {canReconnect && onReconnect && (
              <Menu.Item data-action="reconnect" data-account-id={accountId} onClick={handleMenuAction}>
                {reconnectLabel}
              </Menu.Item>
            )}
            {onSetDefault && (
              <Menu.Item
                data-action="default"
                data-account-id={accountId}
                onClick={handleMenuAction}
                disabled={!canSetDefault}
              >
                {defaultLabel}
              </Menu.Item>
            )}
            {onDelete && (
              <>
                <Menu.Divider />
                <Menu.Item data-action="delete" data-account-id={accountId} onClick={handleMenuAction} color="red">
                  Remover
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    );
  };

  return (
    <Table.ScrollContainer minWidth={700}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Email</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Padrão</Table.Th>
            <Table.Th>Ações</Table.Th>
          </Table.Tr>
        </Table.Thead>

        {loading ? (
          <TableSkeleton />
        ) : accounts.length === 0 ? (
          <EmptyState />
        ) : (
          <Table.Tbody>
            {accounts.map((account) => (
              <Table.Tr key={account.id}>
                {/* Email column */}
                <Table.Td>
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>
                      {account.email}
                    </Text>
                    {account.display_name && (
                      <Text size="xs" c="dimmed">
                        {account.display_name}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>

                {/* Provider column */}
                <Table.Td>
                  <ProviderIcon provider={account.provider} />
                </Table.Td>

                {/* Status column */}
                <Table.Td>
                  <EmailAccountStatusBadge status={account.status} />
                </Table.Td>

                {/* Default column */}
                <Table.Td>
                  {account.is_default && (
                    <Badge color="yellow" variant="light" size="sm">
                      Padrão
                    </Badge>
                  )}
                </Table.Td>

                {/* Actions column */}
                <Table.Td>{renderActions(account)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        )}
      </Table>
    </Table.ScrollContainer>
  );
}

EmailAccountsTable.displayName = "EmailAccountsTable";
